import { memo, type PointerEvent } from "react";
import { useTranslation } from "react-i18next";

interface KioskKeypadProps {
  onPress: (val: string) => void;
  disabled?: boolean;
}

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

/**
 * Memoized numeric keypad for the kiosk PIN screen.
 * Extracted from KioskPage to prevent full-page re-renders on each key press.
 * Visual classes are identical to the original inline keypad.
 * Removed `active:scale-95` and `transition-transform transition-colors`
 * to eliminate costly transform animations on iPad WebKit.
 */
export const KioskKeypad = memo(({ onPress, disabled }: KioskKeypadProps) => {
  const { t } = useTranslation();

  const handlePointerDown = (val: string) => (e: PointerEvent) => {
    e.preventDefault();
    if (!disabled) onPress(val);
  };

  return (
    <div className="grid grid-cols-3 gap-4">
      {DIGITS.map(num => (
        <button
          key={num}
          type="button"
          onPointerDown={handlePointerDown(num)}
          className="h-16 md:h-18 bg-slate-900/80 border border-slate-800/40 rounded-2xl flex items-center justify-center text-2xl font-bold active:bg-indigo-600 active:border-indigo-500 text-white"
        >
          {num}
        </button>
      ))}
      <button
        type="button"
        onPointerDown={handlePointerDown('clear')}
        className="h-16 md:h-18 bg-slate-950 border border-slate-900/50 rounded-2xl flex items-center justify-center text-sm font-bold uppercase tracking-wider text-slate-400 active:bg-slate-800"
      >
        {t('kiosk.ui.clear')}
      </button>
      <button
        type="button"
        onPointerDown={handlePointerDown('0')}
        className="h-16 md:h-18 bg-slate-900/80 border border-slate-800/40 rounded-2xl flex items-center justify-center text-2xl font-bold active:bg-indigo-600 active:border-indigo-500 text-white"
      >
        0
      </button>
      <button
        type="button"
        onPointerDown={handlePointerDown('delete')}
        className="h-16 md:h-18 bg-slate-950 border border-slate-900/50 rounded-2xl flex items-center justify-center text-slate-400 active:bg-slate-800"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.41-6.41A2 2 0 0010.83 5H19a2 2 0 012 2v10a2 2 0 01-2 2h-8.17a2 2 0 01-1.42-.59L3 12z" />
        </svg>
      </button>
    </div>
  );
});
KioskKeypad.displayName = 'KioskKeypad';
